<template>
    <PageSection>
        <PageHeader title="Purchase Approvals"></PageHeader>
        <div class="grid grid-cols-1  md:grid-cols-12 gap-4" v-if="isLoaded">
            <div class="flex gap-4 md:col-span-8">
                <div v-for="(book, index) in bookRequests" class="card border border-purple-100 flex gap-4 p-6">
                    <BookInformation :book="(book as BookDetails)" v-if="book" />
                    <div class="space-y-2" v-if="showLicenseOptions">
                        <h2 class="font-bold text-lg">Select {{ getSpelling("licence", "license") }}</h2>
                        <span class="text-sm text-gray-800" v-if="book.pricings.length > 0">All prices
                            shown are
                            pre-tax</span>
                        <span class="text-sm text-gray-800" v-else>No prices available.</span>
                    </div>
                    <LicenseOptions :licenseOptions="licenseOptions[book.id] ?? []" :id="String(index)"
                        :selectedOption="userSelectedPricePerRequests[book.id] ?? ''"
                        @optionSelected="(option) => setLicense({ id: book.id, license: option })"
                        v-if="showLicenseOptions && book.pricings.length > 0" />
                </div>
            </div>
            <div class="order-first md:col-span-4 md:order-none">
                <div class="card border border-purple-100 p-6 space-y-6 md:flex-grow-0 md:top-0 md:sticky">
                    <h2 class="font-bold text-lg">Summary</h2>
                    <div class="grid grid-cols-2">
                        <template v-for="row in summaryTable">
                            <span class="text-sm text-gray-800">{{ row.title }}</span>
                            <span class="font-bold text-right">{{ row.value }}</span>
                        </template>
                        <template v-if="showLicenseOptions">
                            <div class="col-span-2 border-t border-purple-100 py-1"></div>
                            <span class="text-sm text-gray-800">Total Price (Pre-tax):</span>
                            <span class="font-bold text-right">{{ mapCurrency(currency) }}{{
                                totalPrice }}</span>
                        </template>
                    </div>
                    <div class="flex flex-row-reverse md:flex-col-reverse gap-2 items-end lg:flex-row-reverse">
                        <Button @click="submitPurchase">{{ confirmationButtonText }}</Button>
                        <Button @click="cancelPurchase" :variant="'secondary'">Back to requests</Button>
                    </div>
                </div>
            </div>
        </div>
        <LoadingSpinner class="h-screen justify-center" v-else />
    </PageSection>
</template>
<script setup lang="ts">
import { definePage } from "vue-router/auto";
import BookInformation from "../../../../../shared/components/Book/BookInformation.vue";
import Button from "../../../../../shared/components/Button.vue";
import LoadingSpinner from "../../../../../shared/components/LoadingSpinner.vue";
import PageSection from "../../../../../shared/components/PageSection.vue";
import PageHeader from "../../../components/PageHeader.vue";
import LicenseOptions from "../../../components/Search/LicenseOptions.vue";
import {
	BookDetails,
	useOrderApproval,
} from "../../../composables/useOrderApproval";
import { usePermission } from "../../../composables/usePermission";
import {
	type InstitutionConfig,
	useInstitution,
} from "../../../stores/institution";
import { mapCurrency } from "../../../utils/catalog-search";
import { getSpelling } from "../../../utils/localization";

const router = useRouter();
const institution = useInstitution();
const { getUserPermissions, hasPermission } = usePermission();
const userPermissions = await getUserPermissions();

const {
	bookRequests,
	numberOfSelectedRequests,
	totalFTEs,
	approveBooklistRequests,
	setLicense,
	setInstitutionBooklistConfig,
	isLoaded,
	currency,
	totalPrice,
	showLicenseOptions,
	isRedirecting,
	isSubmitted,
	licenseOptions,
	userSelectedPricePerRequests,
} = useOrderApproval();

const config = institution.institutionConfig ?? ({} as InstitutionConfig);

setInstitutionBooklistConfig({
	userPermissions,
	institutionConfig: config,
});

const hasPurchasePermission =
	["pricing_approvals", "pricing_adoption_approvals"].includes(
		config.approval_type,
	) && hasPermission(userPermissions, "booklist:approve", true);
const confirmationButtonText = hasPurchasePermission
	? "Approve Purchase"
	: "Choose pricing";

if (isRedirecting.value) {
	router.push("/requests");
}

watch(isSubmitted, () => {
	if (isSubmitted) {
		router.push("/requests");
	}
});

watch(isRedirecting, () => {
	if (isRedirecting) {
		router.push("/requests");
	}
});

const summaryTable = computed(() => {
	return [
		{
			title: "Total Books",
			value: numberOfSelectedRequests.value,
		},
		{
			title: `Total FTEs/${getSpelling("Enrolments", "Enrollments")}`,
			value: totalFTEs.value,
		},
	];
});

function cancelPurchase() {
	router.push("/requests");
}

function submitPurchase() {
	approveBooklistRequests({
		// toast action to be called after the request is approved successfully
		buttonAction: () => {
			router.push("/booklist");
		},
	});
}

definePage({
	meta: {
		requiresAuth: true,
		customPermission: "hasRequestsPageAccess",
	},
});
</script>
